import React, { useEffect, useState } from 'react'
import { apiGetFile } from '../generic/Api_Functions'
import { resolveDeviceTypeIllustration } from './deviceTypeImage'

// The cached thumbnail can be briefly unavailable right after the image is (re)marked as
// main (server commit/availability window) — more likely for larger images. Retry with a
// short backoff before falling back to the type illustration, so a just-marked image
// appears without a manual reload (mirrors MainImageThumbnail.jsx).
const THUMBNAIL_RETRY_DELAYS_MS = [500, 1000, 1500]

/**
 * DeviceHeroImage — shows the device's user-marked MAIN IMAGE (story 35 / device main
 * image) when it has one, falling back to the bundled per-type illustration otherwise.
 *
 * Presence is resolved UP FRONT by the screen via the batch `POST devices/mainImages`
 * (sun contract note: never one call per card) and passed in as `mainImageInfo`
 * (`{ hasMainImage, uuid, extension, ... }`). Only when `hasMainImage` is true does this
 * load the cached thumbnail (`GET device/{id}/mainImage/thumbnail`) — so devices without
 * an image make no extra request at all. The thumbnail URL is keyed by the document
 * `uuid` so re-marking a different image busts the cache.
 *
 * Fills its container exactly like the illustration components (`title` = alt text).
 */
export default function DeviceHeroImage ({ device, mainImageInfo = {}, title = '' }) {
  const [thumbUrl, setThumbUrl] = useState('')
  const [failed, setFailed] = useState(false)
  const deviceId = device?.id
  const hasMainImage = Boolean(mainImageInfo?.hasMainImage)
  const uuid = mainImageInfo?.uuid || ''
  const extension = (mainImageInfo?.extension || '').toLowerCase()

  useEffect(() => {
    if (!hasMainImage || deviceId === undefined || deviceId === null) {
      setThumbUrl('')
      setFailed(false)
      return
    }
    let isCurrent = true
    let objectUrl = ''
    setFailed(false)
    // GIFs lose their animation in the server-side thumbnail (ImageIO flattens them to a
    // single JPEG frame). Load the ORIGINAL document instead to keep the animation —
    // same treatment as the part-reference thumbnail (MainImageThumbnail.jsx).
    const base = 'device/' + deviceId + '/mainImage/thumbnail'
    const path = (extension === 'gif' && uuid)
      ? 'document/file/download/' + uuid
      : (uuid ? base + '?v=' + encodeURIComponent(uuid) : base)
    const loadThumb = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const response = await apiGetFile(path)
          const blob = await response.blob()
          if (!isCurrent) return
          objectUrl = URL.createObjectURL(blob)
          setThumbUrl(objectUrl)
          return
        } catch (error) {
          if (!isCurrent) return
          // Retry while the thumbnail is still being made available after a (re)mark.
          if (attempt < THUMBNAIL_RETRY_DELAYS_MS.length) {
            await new Promise((resolve) => setTimeout(resolve, THUMBNAIL_RETRY_DELAYS_MS[attempt]))
            if (!isCurrent) return
            continue
          }
          setThumbUrl('')
          setFailed(true)
          return
        }
      }
    }
    loadThumb()
    return () => {
      isCurrent = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [hasMainImage, deviceId, uuid, extension])

  // Device HAS a main image: show it. While the thumbnail is still loading, render a
  // neutral placeholder instead of the generic type photo, so switching views doesn't
  // flash the default image before the real one arrives. Fall back to the type photo
  // only when there is no main image or its load failed.
  if (hasMainImage && !failed) {
    if (thumbUrl) {
      return <img src={thumbUrl} alt={title} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
    }
    return <span aria-hidden style={{ display: 'block', width: '100%', height: '100%', background: 'var(--mcx-surface-high)' }} />
  }
  const Illustration = resolveDeviceTypeIllustration(device?.deviceType?.name)
  return <Illustration title={title} />
}
